import { AuthService } from '../auth/auth.service';
import { EppService } from '../epp/epp.service';
import { AddressPopup } from '../map/map.service';
import { LanguageService } from '../shared/services/language.service';
import { ThemeService } from '../shared/services/theme.service';
import {
  DetailInfo,
  InfoDtgItem,
  RouteFcstService,
  RouteSituation,
} from './route-fcst.service';

import { DatexPipe } from '../shared/datex.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { ColorMappingWarnDangerPipe } from './pipes/color-mapping-warn-danger.pipe';

import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';

import {
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import * as Highcharts from 'highcharts';
import HC_accessibility from 'highcharts/modules/accessibility';
import { HighchartsChartModule } from 'highcharts-angular';
import moment from 'moment';
import { getSunrise, getSunset } from 'sunrise-sunset-js';

HC_accessibility(Highcharts);

export const MODULES = [
  CommonModule,
  MatListModule,
  MatButtonModule,
  MatDividerModule,
  MatFormFieldModule,
  MatSelectModule,
  HighchartsChartModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  DecimalPipe,
  TranslatePipe,
  DatexPipe,
  RoundPipe,
  ColorMappingWarnDangerPipe,
];
@Component({
  selector: 'app-route-fcst-info',
  templateUrl: './route-fcst-info.component.html',
  styleUrls: [
    './route-fcst-info.component.scss',
    './route-fcst.component.scss',
  ],
  providers: [RouteFcstService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class RouteFcstInfoComponent implements OnInit {
  private injector = inject(Injector);
  private auth = inject(AuthService);
  private eppService = inject(EppService);
  private themeService = inject(ThemeService);
  public routeFcstService = inject(RouteFcstService);
  public translate = inject(TranslateService);
  public languageService = inject(LanguageService);

  detailsData = input<DetailInfo>();
  stations = input<Array<any>>();
  lastSelectedAddress = input<AddressPopup>();
  jumpTo = output<RouteSituation>();

  Highcharts = Highcharts; // required
  chartConstructor = 'chart'; // optional string, defaults to 'chart'
  chartCallback = null;
  updateFlag = model<boolean>(false);
  oneToOneFlag = true; // optional boolean, defaults to false
  runOutsideAngular = false; // optional boolean, defaults to false
  chartOptions = signal<Highcharts.Options>({}); // required
  biggestExtend = signal<InfoDtgItem>(undefined);

  colorChart = {
    ttt: {
      // TTT: red
      borderColor: '#F23D3D',
    },
    troad: {
      // T_ROAD: darkblue
      borderColor: '#001540',
    },
    tbridge: {
      // T_ROAD: darkblue
      borderColor: '#b3b3b3',
    },
    td: {
      // Dewpoint: blue
      borderColor: '#399DE6',
    },
  };

  highChartsData: Array<any> = [];
  translation: any;
  stationName = signal('');
  stationInfo;
  selectedStation = signal<any>(undefined);

  // raw eop2 parameter name -> epp 'fe_parameter' name
  private readonly eppParameterMapping = {
    ttt: 'tt',
    td: 'td',
    troad: 'road_rst',
    tbridge: 'bridge_rst',
  };

  eppStations = computed(() =>
    (this.stations() ?? []).filter(
      station => !!this.getEppConfigForStation(station.stationId)
    )
  );

  constructor() {
    this.listenToDetailsDataChanges();
    this.listenToLastSelectedAddressChanges();
    this.listenToThemeChanges();
  }

  ngOnInit(): void {
    if (!this.translation) {
      this.translation = this.translate.instant('METPARA_CHART');
      const eppStations = this.eppStations();
      if (eppStations.length > 0) {
        this.selectedStation.set(eppStations[0]);
        this.chartDataToShow(eppStations[0]);
      }
    }
  }

  onStationChange(event: MatSelectChange): void {
    this.selectedStation.set(event.value);
    this.chartDataToShow(event.value);
  }

  // Only stations that are part of the route forecast product AND have an
  // active EPP entitlement may be shown - find that entitlement's config.
  getEppConfigForStation(
    stationId: string
  ): { parameterSet: string; days: number; hourly: number } | undefined {
    const customerDocument = this.auth.getCustomerDocument();
    if (!customerDocument?.hasOwnProperty('eppProducts')) {
      return undefined;
    }
    for (const product of customerDocument['eppProducts']) {
      if (!this.auth.isProductActive('eppProducts', product.id)) {
        continue;
      }
      const hasStation = (product.stations ?? []).some(
        station => station.stationId === stationId
      );
      if (
        !hasStation ||
        !this.auth.isUserAllowedToAccessToProduct(product.id, stationId)
      ) {
        continue;
      }

      const parameterSets: string[] = product.parameter_sets ?? [];
      const parameterSet = parameterSets.includes('winter')
        ? 'winter'
        : parameterSets.includes('summer')
          ? 'summer'
          : parameterSets[0];

      // pick the resolution with the smallest (finest) hourly interval
      const resolutions: Array<{ days: number; hourly: number }> =
        product.resolutions ?? [];
      const bestResolution = resolutions.reduce<
        { days: number; hourly: number } | undefined
      >(
        (best, current) =>
          !best || current.hourly < best.hourly ? current : best,
        undefined
      );

      if (!parameterSet || !bestResolution) {
        continue;
      }

      return {
        parameterSet,
        days: bestResolution.days,
        hourly: bestResolution.hourly,
      };
    }
    return undefined;
  }

  chartDataToShow(station: any): void {
    this.stationName.set(station.overwriteStationName);

    // lat/lon is only needed for the day/night plot bands
    this.routeFcstService.getStationInfo(station.stationId).subscribe(
      data => {
        if (data['stations']['stationItems'].length > 0) {
          this.stationInfo = data['stations']['stationItems'][0];
        }
      },
      error => console.error('cannot get station information', error)
    );

    const eppConfig = this.getEppConfigForStation(station.stationId);
    if (!eppConfig) {
      return;
    }
    this.eppService
      .getEppData(
        eppConfig.parameterSet,
        +station.stationId,
        eppConfig.days,
        eppConfig.hourly
      )
      .then(eppResponse => {
        this.setChartData(eppResponse);
      })
      .catch(error1 =>
        console.log('Error RouteFcst chartDataToShow getEppData', error1)
      );
  }

  setChartData(eppResponse: any): void {
    this.highChartsData = [];
    const forecast = eppResponse?.forecast ?? [];

    const now = new Date();
    const lastHour = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      0,
      0
    );
    const lastTimestep = moment(lastHour).add(24, 'hours');

    for (const param of Object.keys(this.eppParameterMapping)) {
      const eppParameter = this.eppParameterMapping[param];
      const highChartsData = [];
      for (const dataset of forecast) {
        if (new Date(dataset['valid_at']) < lastHour) {
          continue;
        }
        if (moment(dataset['valid_at']).isAfter(lastTimestep)) {
          continue;
        }
        if (dataset.hasOwnProperty(eppParameter)) {
          highChartsData.push({
            x: Date.parse(dataset.valid_at),
            y: dataset[eppParameter],
          });
        }
      }

      let highChartsPlot;

      const isVisible = true;
      const label =
        this.translation[
          param.indexOf('troad') > -1
            ? 'road_tt'
            : param.indexOf('tbridge') > -1
              ? 'TBridge'
              : param.indexOf('ttt') > -1
                ? 'tt'
                : param.indexOf('td') > -1
                  ? 'Td'
                  : param
        ];

      highChartsPlot = {
        name: label,
        type: 'spline',
        data: highChartsData,
        tooltip: {
          xDateFormat: '%a %d.%m.%Y %H:%M',
          shared: true,
          valueSuffix: ' °C',
        },
        color: this.getSerieColor(param),
        marker: {
          enabled: false,
        },
        visible: isVisible, // this.highChartsData.length < 3, //
      };

      this.highChartsData.push(highChartsPlot);
    }
    this.setHighChartOptions();
  }

  setHighChartOptions(): void {
    const isDark = this.themeService.theme() === 'dark';
    const textColor = isDark ? '#E0E0E3' : '#333333';
    const gridLineColor = isDark ? '#444444' : '#e6e6e6';
    const plotLineColor = isDark ? '#707073' : '#9e9e9e';
    const plotBandColor = isDark ? 'rgba(255, 255, 255, 0.06)' : '#EFEFEF';

    Highcharts.setOptions({
      lang: {
        shortWeekdays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        months: [
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ],
        weekdays: [
          'Sonntag',
          'Montag',
          'Dienstag',
          'Mittwoch',
          'Donnerstag',
          'Freitag',
          'Samstag',
        ],
        shortMonths: [
          'Jan',
          'Feb',
          'Mär',
          'Apr',
          'Mai',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Okt',
          'Nov',
          'Dez',
        ],
        printChart: 'Meteogramm drucken',
        downloadJPEG: 'Download JPEG Bild',
        downloadPNG: 'Download PNG Bild',
        downloadPDF: 'Download PDF Dokument',
        downloadSVG: 'Download SVG Vektorgrafik',
        viewFullscreen: 'Vollbildversion anzeigen',
      },
      time: {
        // timezone: this.eopService.getTimeZone(),
        useUTC: false,
      },
    });

    this.chartOptions.set({
      chart: {
        zooming: { type: 'xy' },
        marginTop: 8,
        backgroundColor: 'transparent',
        style: { fontFamily: 'Roboto, sans-serif' },
        events: {
          beforePrint(): void {
            this.setTitle(
              {
                text: 'MeteoFocus',
              },
              {
                text:
                  'Temperaturen (' + moment().format('DD.MM.YYYY HH:mm') + ')',
                align: 'left',
              }
            );
          },
          afterPrint(): void {
            this.setTitle(
              {
                text: null,
              },
              {
                text: null,
              }
            );
          },
        },
      },
      title: {
        text: '',
      },
      xAxis: [
        {
          type: 'datetime',
          labels: {
            format: '{value:%a %H:%M}',
            style: { color: textColor },
          },
          tickInterval: 3600000 * 6,
          crosshair: true,
          gridLineColor,
          plotBands: this.getPlotBands(plotBandColor),
        },
      ],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { color: textColor },
        itemHoverStyle: { color: isDark ? '#ffffff' : '#000000' },
      },
      yAxis: [
        {
          // Primary yAxis
          gridLineColor,
          labels: {
            x: -5,
            style: { color: textColor },
          },
          title: {
            align: 'high',
            rotation: 0,
            text: '°C',
            offset: 0,
            x: -5,
            y: -10,
            style: { color: textColor },
          },
          softMin: 0,
          softMax: 10,
          maxPadding: 0.005,
          tickPixelInterval: 25,
          plotLines: [
            {
              value: 0,
              color: plotLineColor,
              width: 1,
              zIndex: 2,
            },
          ],
          startOnTick: false,
          // maxPadding: 0.3,
          // minRange: 8,
        },
      ],
      credits: {
        enabled: false,
        text: 'Wettermanufaktur GmbH',
        // imageURL: 'assets/brand/wm_Logo.jpg',
        href: 'https://www.wettermanufaktur.de',
      },
      tooltip: {
        shared: true,
        backgroundColor: isDark
          ? 'rgba(42, 42, 44, 0.9)'
          : 'rgba(255, 255, 255, 0.9)',
        style: { color: textColor },
      },
      series: this.highChartsData,
      exporting: {
        enabled: false,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                floating: false,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                x: 0,
                y: 0,
              },
            },
          },
        ],
      },
    });
  }

  getPlotBands(color: string): Array<any> {
    const plotBands = [];
    for (let dayOffset = 0; dayOffset < 6; dayOffset++) {
      let evening = moment();
      evening = evening.add(dayOffset, 'days').subtract(1, 'days');
      let morning = moment();
      morning = morning.add(dayOffset, 'days');
      const sunset = getSunset(
        this.stationInfo.latitude,
        this.stationInfo.longitude,
        evening.toDate()
      );
      const sunrise = getSunrise(
        this.stationInfo.latitude,
        this.stationInfo.longitude,
        morning.toDate()
      );
      plotBands.push({
        from: sunset,
        to: sunrise,
        color,
      });
    }
    return plotBands;
  }

  getSerieColor(param: string): string {
    if (this.colorChart.hasOwnProperty(param)) {
      return this.colorChart[param].borderColor;
    }
    return 'blue';
  }

  onJumpTo(toDtg: string, toParameter: string): void {
    this.jumpTo.emit({ dtg: moment(toDtg), parameter: toParameter });
  }

  private listenToDetailsDataChanges() {
    effect(
      () => {
        const detailsData = this.detailsData();

        untracked(() => {
          if (detailsData.biggestExtendOfWorstBridgeCondition) {
            this.biggestExtend.set({
              item: Math.max(
                detailsData.biggestExtendOfWorstRoadCondition.item,
                detailsData.biggestExtendOfWorstBridgeCondition.item
              ),
              dtg:
                detailsData.biggestExtendOfWorstRoadCondition.item >=
                detailsData.biggestExtendOfWorstBridgeCondition.item
                  ? detailsData.biggestExtendOfWorstRoadCondition.dtg
                  : detailsData.biggestExtendOfWorstBridgeCondition.dtg,
            });
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToThemeChanges() {
    effect(
      () => {
        this.themeService.theme();

        untracked(() => {
          if (this.highChartsData.length > 0) {
            this.setHighChartOptions();
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToLastSelectedAddressChanges() {
    effect(
      () => {
        const lastSelectedAddress = this.lastSelectedAddress();

        untracked(() => {
          if (
            lastSelectedAddress &&
            lastSelectedAddress.hasOwnProperty('eopData') &&
            lastSelectedAddress.eopData
          ) {
            this.stationName.set(lastSelectedAddress.street);
            this.setChartData(lastSelectedAddress.eopData);
          }
        });
      },
      { injector: this.injector }
    );
  }
}
